import { TOEFLLevel } from './types';

/**
 * TOEFL 等级详细描述
 * TOEFL 总分范围：0-120，每部分0-30
 */
export const TOEFL_LEVEL_DESCRIPTIONS: Record<
  TOEFLLevel,
  {
    name: string;
    cefrEquivalent: string;
    totalScoreRange: string;
    description: string;
    canDo: string[];
  }
> = {
  [TOEFLLevel.HighAdvanced]: {
    name: '高级',
    cefrEquivalent: 'C2',
    totalScoreRange: '103-120',
    description: '能够熟练、准确、流利地运用英语，几乎达到母语者水平。能够理解复杂的学术和职业材料，进行深入的分析和讨论。',
    canDo: [
      '理解复杂的学术文本和专业文献',
      '进行高水平的学术讨论和专业交流',
      '撰写结构严谨、论证充分的学术论文',
      '准确理解英语文化的细微之处',
      '自如地进行复杂的口头表达',
    ],
  },
  [TOEFLLevel.Advanced]: {
    name: '中高级',
    cefrEquivalent: 'C1',
    totalScoreRange: '79-102',
    description: '能够有效、准确地运用英语，在学术和职业环境中表现良好。能够理解复杂的文本和材料，进行有深度的讨论。',
    canDo: [
      '理解学术讲座和专业文本',
      '参与学术讨论和研讨会',
      '撰写结构清晰、论证合理的论文',
      '理解复杂的听力材料，如讲座和演讲',
      '进行有组织的口头表达',
    ],
  },
  [TOEFLLevel.LowAdvanced]: {
    name: '中高级偏上',
    cefrEquivalent: 'B2/C1',
    totalScoreRange: '67-78',
    description: '能够在大多数情况下有效运用英语，在学术和职业环境中表现较好。能够理解复杂文本的主要观点。',
    canDo: [
      '理解主要学术内容，但可能遗漏细节',
      '参与课堂讨论并表达观点',
      '撰写有一定结构但可能不够深入的论文',
      '理解大多数学术讲座的主要内容',
    ],
  },
  [TOEFLLevel.HighIntermediate]: {
    name: '中级偏上',
    cefrEquivalent: 'B2',
    totalScoreRange: '55-66',
    description: '能够较为有效地运用英语，在熟悉的学术和职业环境中表现良好。能够理解复杂文本的主要观点和重要细节。',
    canDo: [
      '理解大部分学术内容',
      '参与讨论，表达基本观点',
      '撰写结构基本清晰的论文',
      '理解学术讲座的主要内容和一些细节',
    ],
  },
  [TOEFLLevel.Intermediate]: {
    name: '中级',
    cefrEquivalent: 'B1/B2',
    totalScoreRange: '44-54',
    description: '能够处理语言需求，在熟悉的情境中表现良好。能够理解标准演讲和日常对话的主要内容。',
    canDo: [
      '理解日常和基本学术材料',
      '进行简单的讨论和交流',
      '撰写基本连贯的文本',
      '理解简单学术讲座的主要观点',
    ],
  },
  [TOEFLLevel.LowIntermediate]: {
    name: '中级偏下',
    cefrEquivalent: 'B1',
    totalScoreRange: '33-43',
    description: '能够在熟悉的情境中运用英语，但在复杂情境中可能遇到困难。能够理解简单文本和日常对话。',
    canDo: [
      '理解简单的学术和日常材料',
      '进行基本的交流',
      '撰写简单的句子和段落',
      '理解简单讲座的主要观点',
    ],
  },
  [TOEFLLevel.HighBeginner]: {
    name: '基础偏上',
    cefrEquivalent: 'A2',
    totalScoreRange: '23-32',
    description: '只能运用非常基础的英语，在熟悉的日常情境中能够进行基本交流。',
    canDo: [
      '理解非常简单的英语',
      '进行最基本的生活交流',
      '书写简单的句子',
    ],
  },
  [TOEFLLevel.LowBeginner]: {
    name: '基础',
    cefrEquivalent: 'A1/A2',
    totalScoreRange: '10-22',
    description: '英语能力非常有限，只能理解和使用少量英语短语和表达。',
    canDo: [
      '理解极少量的英语',
      '使用一些固定短语',
    ],
  },
  [TOEFLLevel.Beginner]: {
    name: '入门',
    cefrEquivalent: 'A1',
    totalScoreRange: '0-9',
    description: '几乎不能理解或使用英语，无法进行有效交流。',
    canDo: [],
  },
};

/**
 * 根据总分获取等级
 */
export function getLevelByTotalScore(totalScore: number): TOEFLLevel {
  if (totalScore >= 103) return TOEFLLevel.HighAdvanced;
  if (totalScore >= 79) return TOEFLLevel.Advanced;
  if (totalScore >= 67) return TOEFLLevel.LowAdvanced;
  if (totalScore >= 55) return TOEFLLevel.HighIntermediate;
  if (totalScore >= 44) return TOEFLLevel.Intermediate;
  if (totalScore >= 33) return TOEFLLevel.LowIntermediate;
  if (totalScore >= 23) return TOEFLLevel.HighBeginner;
  if (totalScore >= 10) return TOEFLLevel.LowBeginner;
  return TOEFLLevel.Beginner;
}

/**
 * 获取等级描述
 */
export function getLevelDescription(level: TOEFLLevel) {
  return TOEFL_LEVEL_DESCRIPTIONS[level];
}

/**
 * 获取下一个等级
 */
export function getNextLevel(currentLevel: TOEFLLevel): TOEFLLevel | null {
  const levels = Object.values(TOEFLLevel);
  const currentIndex = levels.indexOf(currentLevel);
  if (currentIndex < levels.length - 1) {
    return levels[currentIndex + 1];
  }
  return null;
}

/**
 * 比较两个等级
 * 返回 -1 如果 a < b，0 如果 a = b，1 如果 a > b
 */
export function compareLevels(a: TOEFLLevel, b: TOEFLLevel): number {
  const levels = Object.values(TOEFLLevel);
  const indexA = levels.indexOf(a);
  const indexB = levels.indexOf(b);
  return Math.sign(indexA - indexB);
}
